<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useNotificationsStore } from '@/store/notifications'
import { useI18n } from 'vue-i18n'
import Popper from 'vue3-popper'
import {
  DBtn,
  DTextField,
  DSelectFieldSearch,
  DToggleField,
  DTable,
  DModal,
  DLoading,
  SearchMdIcon,
  Edit03Icon,
  Lock03Icon,
  LockUnlocked03Icon
} from 'front-library'
import {
  getTemplates,
  getCountryOptions,
  activateTemplate,
  inactivateTemplate,
  BankTemplateSummary,
  SelectOption
} from '@/app/core/services/bank-templates'

/* store, router, notifications */
const router = useRouter()
const store = useUserStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const descriptionModal = ref('')
const typeAlertModal = ref('')
const acceptQuestion = ref('')

const loading = ref(false)
const messageLoading = ref('')

/* Datos */
const search = ref('')
const tableTemplates = ref(null)
const templateUpdateState = ref<BankTemplateSummary | null>(null)

/* Filtros: pais (todos por defecto) e inactivas (ocultas por defecto) */
const countryFilter = ref('')
const searchCountry = ref('')
const includeInactive = ref(false)
const countries = ref<SelectOption[]>([])

const filterOptions = computed(
  () => (items: SelectOption[], term: string) =>
    items.filter(item =>
      item.name.toLowerCase().includes(term.toString().toLowerCase())
    )
)

/* Primera opcion vacia = todos los paises */
const countryOptions = computed<SelectOption[]>(() => [
  { id: '', name: t('bankTemplates.list.filters.allCountries') },
  ...countries.value
])

/* Fila que consume DTable (los slots reciben estos campos) */
interface TemplateRow {
  id: string
  name: string
  bank_name: string
  country_id: string
  country: string
  publishedVersion: number | null
  hasDraft: boolean
  is_inactive: boolean
  updated_at: string
}

/* Datos componente table */
const columns = ref([
  { name: 'name', title: t('bankTemplates.list.table.template'), table: 'name' },
  {
    name: 'bank_name',
    title: t('bankTemplates.list.table.bank'),
    table: 'bank_name'
  },
  {
    name: 'country',
    title: t('bankTemplates.list.table.country'),
    table: 'country'
  },
  { name: 'version', title: t('bankTemplates.list.table.version') },
  { name: 'state', title: t('bankTemplates.list.table.status') },
  {
    name: 'updated_at',
    title: t('bankTemplates.list.table.updatedAt'),
    table: 'updated_at'
  },
  { name: 'actions', title: '' }
])
/* Listado completo (todos los paises); los filtros se aplican en memoria */
const allRows = ref<TemplateRow[]>([])
const rows = ref<TemplateRow[]>([])
const totalRows = ref(0)

const applyFilters = () => {
  const term = search.value.trim().toLowerCase()
  rows.value = allRows.value.filter(
    row =>
      (includeInactive.value || !row.is_inactive) &&
      (!countryFilter.value || row.country_id === countryFilter.value) &&
      (!term ||
        row.name.toLowerCase().includes(term) ||
        row.bank_name.toLowerCase().includes(term))
  )
  totalRows.value = rows.value.length
}

/* Redirect's */
const createTemplate = () => {
  void router.push('/bank-template')
}
const openTemplate = (id: string) => {
  void router.push(`/bank-template/${id}`)
}

/* Cambiar estado de la plantilla (activar / inactivar por completo) */
const changeStateTemplate = (row: TemplateRow) => {
  templateUpdateState.value = {
    id: row.id,
    name: row.name,
    bankName: row.bank_name,
    countryId: row.country_id,
    country: row.country,
    publishedVersion: row.publishedVersion,
    hasDraft: row.hasDraft,
    active: !row.is_inactive,
    updatedAt: row.updated_at
  }
  titleModal.value = row.is_inactive
    ? t('bankTemplates.toggleActive.activateTitle', { name: row.name })
    : t('bankTemplates.toggleActive.deactivateTitle', { name: row.name })
  descriptionModal.value = row.is_inactive
    ? t('bankTemplates.toggleActive.activateDescription')
    : t('bankTemplates.toggleActive.deactivateDescription')
  typeAlertModal.value = 'question'
  acceptQuestion.value = row.is_inactive
    ? t('bankTemplates.list.actions.activate')
    : t('bankTemplates.list.actions.deactivate')
  showModal.value = true
}

const changeStateConfirm = async () => {
  const target = templateUpdateState.value
  showModal.value = false
  if (!target) return
  loading.value = true
  messageLoading.value = ''
  try {
    await (target.active
      ? inactivateTemplate(target.id)
      : activateTemplate(target.id))
    notifications.addNotification({
      title: target.active
        ? t('bankTemplates.notification.deactivatedTitle')
        : t('bankTemplates.notification.activatedTitle'),
      text: target.active
        ? t('bankTemplates.notification.deactivatedText', { name: target.name })
        : t('bankTemplates.notification.activatedText', { name: target.name }),
      type: 'success',
      time: 10000
    })
    await templates()
  } catch (error: any) {
    showError(error)
  } finally {
    loading.value = false
  }
}

/* Cerrar el componente Modal */
const closeModal = () => {
  showModal.value = false
}

/* Listar plantillas */
const templates = async () => {
  loading.value = true
  messageLoading.value = t('loading.data')
  try {
    /* Listado completo, independiente de la nomina/pais seleccionado */
    const data = await getTemplates()
    allRows.value = data.map(item => ({
      id: item.id,
      name: item.name,
      bank_name: item.bankName,
      country_id: item.countryId,
      country: item.country,
      publishedVersion: item.publishedVersion,
      hasDraft: item.hasDraft,
      is_inactive: !item.active,
      updated_at: item.updatedAt
    }))
    applyFilters()
    loading.value = false
  } catch (error: any) {
    showError(error)
  }
}

const showError = (error: any) => {
  loading.value = false
  notifications.addNotification({
    title: 'Error',
    text: error?.message ?? String(error),
    type: 'error',
    time: 10000
  })
}

watch([countryFilter, includeInactive], applyFilters)

void templates()
getCountryOptions()
  .then(options => (countries.value = options))
  .catch(showError)
</script>
<template>
  <DLoading v-show="loading" :message="messageLoading" />
  <DModal
    :open="showModal"
    :typeAlert="typeAlertModal"
    :title="titleModal"
    :description="descriptionModal"
    :acceptQuestion="acceptQuestion"
    @closeModal="closeModal"
    @otherMethod="changeStateConfirm" />
  <div class="h-full px-5 overflow-auto scroll">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow"
          v-html="t('bankTemplates.title')" />
        <p
          class="text-gray-500 text-shadow"
          v-html="t('bankTemplates.description')" />
      </div>
      <div class="sm:flex justify-between items-end gap-4 pt-8">
        <div class="flex flex-wrap items-end gap-2 mb-3 sm:mb-0 flex-auto">
          <DTextField
            :placeholder="t('form.button.search') + '...'"
            :icon="'SearchLgIcon'"
            class="w-full sm:w-80"
            v-model="search"
            @keyup.enter="applyFilters" />
          <DSelectFieldSearch
            class="w-full sm:w-60"
            :label="t('bankTemplates.list.filters.country')"
            v-model="countryFilter"
            :placeholder="t('bankTemplates.list.filters.allCountries')"
            :options="filterOptions(countryOptions, searchCountry)"
            @filter="searchCountry = $event" />
          <DToggleField
            class="mx-2 mb-2 flex-shrink-0"
            :label="t('bankTemplates.list.filters.includeInactive')"
            v-model="includeInactive" />
          <DBtn
            @click.prevent="applyFilters"
            v-html="t('form.button.search')" />
        </div>
        <div class="flex gap-2">
          <DBtn
            :icon="'PlusIcon'"
            type="button"
            @click="createTemplate()"
            v-if="store.hasPermission('bank_template:create')">
            {{ t('bankTemplates.list.newTemplate') }}
          </DBtn>
        </div>
      </div>
    </div>
    <DTable
      ref="tableTemplates"
      :columns="columns"
      :rows="rows"
      :total-rows="totalRows"
      :select-options="false"
      :text-no-data="t('bankTemplates.list.noResults')"
      :text-per-page="t('table.rowsPerPage')">
      <!-- Version publicada (verde, solo "V2") + borrador (ambar) en la misma columna -->
      <template #version="data: any">
        <div class="flex items-center gap-2 py-2.5">
          <div
            v-if="data.publishedVersion !== null"
            class="flex items-center rounded-full font-semibold w-fit text-sm py-1 px-3 bg-green-100 text-green-600">
            <div class="mr-2 w-1.5 h-1.5 rounded-full bg-green-600"></div>
            {{ t('bankTemplates.badges.version', { version: data.publishedVersion }) }}
          </div>
          <div
            v-if="data.hasDraft"
            class="flex items-center rounded-full font-semibold w-fit text-sm py-1 px-3 bg-yellow-100 text-yellow-600">
            <div class="mr-2 w-1.5 h-1.5 rounded-full bg-yellow-500"></div>
            {{ t('bankTemplates.badges.draft') }}
          </div>
          <span
            v-if="data.publishedVersion === null && !data.hasDraft"
            class="text-gray-300">
            —
          </span>
        </div>
      </template>
      <!-- Estado: inactiva en rojo, activa en verde (mismo estilo del sistema) -->
      <template #state="data: any">
        <div
          class="flex items-center rounded-full font-semibold w-fit text-sm py-1 px-3"
          :class="
            data.is_inactive
              ? 'bg-red-100 text-red-600'
              : 'bg-green-100 text-green-600'
          ">
          <div
            class="mr-2 w-1.5 h-1.5 rounded-full"
            :class="data.is_inactive ? 'bg-red-600' : 'bg-green-600'"></div>
          {{
            data.is_inactive
              ? t('bankTemplates.badges.inactive')
              : t('bankTemplates.badges.active')
          }}
        </div>
      </template>
      <template #actions="data: any">
        <div class="flex py-2.5 justify-end gap-1">
          <Popper
            v-if="store.hasPermission('bank_template:list')"
            arrow
            offsetDistance="12"
            :content="t('table.action.detail')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="openTemplate(data.id)">
              <SearchMdIcon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="
              !data.is_inactive &&
              store.hasPermission('bank_template:update')
            "
            arrow
            offsetDistance="12"
            :content="t('table.action.edit')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="openTemplate(data.id)">
              <Edit03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="store.hasPermission('bank_template:update')"
            arrow
            offsetDistance="12"
            :content="
              data.is_inactive
                ? t('bankTemplates.list.actions.activate')
                : t('bankTemplates.list.actions.deactivate')
            "
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="changeStateTemplate(data)">
              <Lock03Icon
                v-if="data.is_inactive"
                class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
              <LockUnlocked03Icon
                v-else
                class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
        </div>
      </template>
    </DTable>
  </div>
</template>
